import { useResizeDetector } from 'react-resize-detector'
import { Outlet } from 'react-router-dom'

import { useTheme } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'

import { FreshworkWidget } from '@/components/ui/widgets/FreshworkWidget/FreshWorkWidget'
import SubscriptionChecker from '@/components/layout/SubscriptionChecker'
import HeartbeatLoader from '@/components/ui/loaders/HeartbeatLoader'
import { IdleWarningDialog } from '@/components/auth/IdleWarningDialog'
import { ElaraPanelContent } from '@/components/elara/ElaraPanel'
import { AbilityProvider } from '@/providers/AbilityProvider'
import { useElaraPanel } from '@/providers/elara/ElaraPanelContext'
import { useEmergencyRoomAlerts } from '@/hooks/useEmergencyRoomAlerts'

import { Header } from './Header'
import { Footer } from './Footer'
import { Sidebar } from './Sidebar'
import { SkipToMainContentLink } from './SkipToMainContentLink'
import { useFreshworkWidget } from '../model/useFreshworkWidget'
import { useIdleWarning } from '../model/useIdleWarning'
import { useSidebarItems } from '../model/useSidebarItems'

export type MainLayoutProps = {}

export function MainLayout(props: MainLayoutProps) {
  const { width: pageContainerWidth, ref: pageContainerRef } = useResizeDetector()
  const theme = useTheme()
  const { sidebarItems, isLoadingLicenses, canUseElaraChat } = useSidebarItems()
  const idleWarning = useIdleWarning()
  const freshwork = useFreshworkWidget()
  const elaraPanel = useElaraPanel()
  const isPanelOpen = elaraPanel?.isPanelOpen ?? false

  // SEHA (en-SA): sound + visual alert for doctors when a new ER request arrives.
  useEmergencyRoomAlerts()

  return (
    <AbilityProvider>
      {/* 100dvh tracks the visible viewport on mobile (URL bar collapse); 100vh is the fallback for older browsers. */}
      <Box
        ref={pageContainerRef}
        sx={{
          height: '100vh',
          '@supports (height: 100dvh)': { height: '100dvh' },
          overflow: 'hidden',
          backgroundColor: theme.palette.background.default,
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        <SkipToMainContentLink />
        <IdleWarningDialog open={idleWarning.open} initialSeconds={idleWarning.seconds} onStay={idleWarning.onStay} />
        <Stack direction={{ xs: 'column', md: 'row' }} sx={{ height: '100%' }}>
          {(pageContainerWidth || 0) >= theme.breakpoints.values.md && <Sidebar isLoading={isLoadingLicenses} menuItems={sidebarItems} />}
          <Stack sx={{ flex: 1, position: 'relative', minWidth: 0, height: '100%', overflow: 'auto' }}>
            <Header menuItems={sidebarItems} />
            {freshwork.show && <FreshworkWidget customer={freshwork.customer} language={freshwork.language} />}
            <Box component="main" id="main-content" sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              {isLoadingLicenses ? (
                <Stack flex={1} alignItems="center" justifyContent="center">
                  <HeartbeatLoader sx={{ transform: 'scale(4)' }} />
                </Stack>
              ) : (
                <SubscriptionChecker subscriptionSidebarItems={sidebarItems}>
                  <Outlet />
                </SubscriptionChecker>
              )}
            </Box>
            <Footer />
          </Stack>
          {isPanelOpen && canUseElaraChat && <ElaraPanelContent />}
        </Stack>
        <div
          aria-live="polite"
          aria-atomic="true"
          style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', overflow: 'hidden' }}
          id="status-announcer"
        />
      </Box>
    </AbilityProvider>
  )
}
